import { StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import Svg from 'react-native-svg';
import { tokens } from '../../theme/tokens';
import type {
  KnowledgeGraphEdgeViewModel,
  KnowledgeGraphNodeViewModel,
} from '../../types/learningAnalysis';
import {
  KNOWLEDGE_GRAPH_CANVAS_HEIGHT,
  KNOWLEDGE_GRAPH_CANVAS_WIDTH,
  createKnowledgeGraphEdges,
  resolveKnowledgeGraphEdgeLine,
  resolveKnowledgeGraphLayout,
  type KnowledgeGraphLayoutNode,
} from '../../utils/knowledgeGraphLayout';
import { KnowledgeGraphEdge } from './KnowledgeGraphEdge';
import { KnowledgeGraphNode } from './KnowledgeGraphNode';

export {
  createKnowledgeGraphEdges,
  resolveKnowledgeGraphLayout as buildKnowledgeGraphLayout,
} from '../../utils/knowledgeGraphLayout';

type KnowledgeGraphCanvasProps = {
  nodes: KnowledgeGraphNodeViewModel[];
  edges?: KnowledgeGraphEdgeViewModel[];
  selectedNodeId?: string | null;
  onSelectNode?: (node: KnowledgeGraphLayoutNode) => void;
  compact?: boolean;
  height?: number;
  scale?: number;
  style?: StyleProp<ViewStyle>;
};

export function KnowledgeGraphCanvas({
  nodes,
  edges,
  selectedNodeId,
  onSelectNode,
  compact = false,
  height = 230,
  scale = 1,
  style,
}: KnowledgeGraphCanvasProps) {
  const layoutNodes = resolveKnowledgeGraphLayout(nodes, compact);
  const nodeById = new Map(layoutNodes.map((node) => [node.id, node]));
  const graphEdges = edges ?? createKnowledgeGraphEdges(layoutNodes);
  const selectedNode = selectedNodeId ? nodeById.get(selectedNodeId) : null;

  return (
    <View style={[styles.canvas, { height }, style]}>
      <View
        style={[
          styles.scaledCanvas,
          {
            width: KNOWLEDGE_GRAPH_CANVAS_WIDTH,
            height: KNOWLEDGE_GRAPH_CANVAS_HEIGHT,
            transform: [{ scale }],
          },
        ]}
      >
        <Svg
          width={KNOWLEDGE_GRAPH_CANVAS_WIDTH}
          height={KNOWLEDGE_GRAPH_CANVAS_HEIGHT}
          style={StyleSheet.absoluteFill}
        >
          {graphEdges.map((edge) => {
            const source = nodeById.get(edge.sourceId);
            const target = nodeById.get(edge.targetId);
            if (!source || !target) {
              return null;
            }

            const active =
              selectedNode?.id === source.id ||
              selectedNode?.id === target.id ||
              selectedNodeId == null;
            const line = resolveKnowledgeGraphEdgeLine(source, target);

            return (
              <KnowledgeGraphEdge
                key={edge.id}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                active={active}
                sourceStatus={source.status}
                targetStatus={target.status}
              />
            );
          })}
        </Svg>
        {layoutNodes.map((node) => (
          <KnowledgeGraphNode
            key={node.id}
            node={node}
            compact={compact}
            selected={selectedNodeId === node.id}
            onPress={(nextNode) => onSelectNode?.(nextNode)}
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  canvas: {
    borderRadius: tokens.radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: tokens.colors.background,
  },
  scaledCanvas: {
    position: 'relative',
  },
});
